import { buildQueryString } from '../utils/stringUtils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAnalytics } from './useAnalytics';
import { AnalyticsEvent } from '../analytics';

const KEEP_ALIVE_TIMER = 30 * 1000; // 30 seconds
const KEEP_ALIVE_TIMEOUT = 1 * 60 * 1000; // 1 minute

const DEFAULT_PROGRESS: ScenarioPathsProgressPayload = {
  percent: 0,
  step: 'Waiting for server...',
  total_steps: 0,
  completed_steps: 0,
};

const IDLE_PROGRESS: ScenarioPathsProgressPayload = {
  percent: 0,
  step: 'Path service connected',
  total_steps: 0,
  completed_steps: 0,
};

const READY_PROGRESS: ScenarioPathsProgressPayload = {
  percent: 100,
  step: 'Build complete',
  total_steps: 0,
  completed_steps: 0,
};

export enum ScenarioPathsMessageTypes {
  UPDATE = 'UPDATE',
  ERROR = 'ERROR',
  PROGRESS = 'PROGRESS',
  PING = 'PING',
}

export enum ScenarioPathsActions {
  PING = 'ping',
  BUILD = 'build',
  GET = 'get',
}

export enum ScenarioPathsStatus {
  INITIALIZING = 'INITIALIZING',
  IDLE = 'IDLE',
  RUNNING = 'RUNNING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  // The build stopped because the path service deleted the scenario's state, for
  // example after a save; nothing is built. The update's message gives the reason.
  CANCELLED = 'CANCELLED',
}

export type ScenarioPathsMessage = {
  type: ScenarioPathsMessageTypes;
};

export type ScenarioPathsUpdateMessage = ScenarioPathsMessage & {
  status: ScenarioPathsStatus;
  message: string;
};

export type ScenarioPathsErrorMessage = ScenarioPathsUpdateMessage;

export type ScenarioPathsProgressPayload = {
  percent: number;
  step: string;
  total_steps: number;
  completed_steps: number;
};

export type ScenarioPathsProgressMessage = ScenarioPathsMessage & {
  progress: ScenarioPathsProgressPayload;
  status: ScenarioPathsStatus;
};

export type ScenarioPathsState = {
  ready: boolean;
  progress: ScenarioPathsProgressPayload;
  status: ScenarioPathsStatus;
  message: string | null;
  serviceError: string | null;
  socketError: string | null;
};

export type UseScenarioPathsResult = {
  build: () => void;
} & ScenarioPathsState;

export const useScenarioPaths = (
  scenarioId?: string | number | null,
  skip?: boolean,
  autoReconnect: boolean = true,
) => {
  const socket = useRef<WebSocket | null>(null);
  const [progress, setProgress] =
    useState<ScenarioPathsProgressPayload>(DEFAULT_PROGRESS);
  const [status, setStatus] = useState<ScenarioPathsStatus>(
    ScenarioPathsStatus.INITIALIZING,
  );
  const [message, setMessage] = useState<string | null>(null);
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [socketError, setSocketError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const analytics = useAnalytics();

  const keepAliveTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const keepAliveTimer = useRef<NodeJS.Timeout | undefined>(undefined);

  const pingServer = useCallback(() => {
    clearTimeout(keepAliveTimer.current);
    keepAliveTimer.current = setTimeout(() => {
      if (socket.current?.readyState !== WebSocket.OPEN) return;
      socket.current.send(
        JSON.stringify({ action: ScenarioPathsActions.PING }),
      );
      clearTimeout(keepAliveTimeout.current);
      keepAliveTimeout.current = setTimeout(() => {
        setSocketError('Paths service stopped responding');
        setStatus(ScenarioPathsStatus.ERROR);
        socket.current?.close();
      }, KEEP_ALIVE_TIMEOUT);
    }, KEEP_ALIVE_TIMER);
  }, [socket.current]);

  const connect = useCallback(() => {
    if (skip || !scenarioId || scenarioId === 'new') return;
    const queryString = buildQueryString({
      scenario_id: scenarioId,
      access_token: localStorage.getItem('token') || '',
    });
    const wsEndpoint = `${process.env.REACT_APP_STREAMING_API_URL}/ws/path-report/?${queryString}`;
    const ws = new WebSocket(wsEndpoint);

    ws.onopen = () => {
      setIsLoading(false);
      setServiceError(null);
      setSocketError(null);
      setProgress(IDLE_PROGRESS);
      ws.send(JSON.stringify({ action: ScenarioPathsActions.GET }));
    };
    socket.current = ws;
    return ws;
  }, [scenarioId, skip]);

  useEffect(() => {
    connect();
    return () => {
      setProgress(DEFAULT_PROGRESS);
      setStatus(ScenarioPathsStatus.INITIALIZING);
      setMessage(null);
      setServiceError(null);
      setSocketError(null);
      setIsLoading(true);
      clearTimeout(keepAliveTimer.current);
      clearTimeout(keepAliveTimeout.current);
    };
  }, [connect]);

  useEffect(() => {
    pingServer();
  }, [pingServer]);

  const reconnect = useCallback(() => {
    if (socket.current && socket.current.readyState === WebSocket.OPEN) {
      socket.current.close();
    }
    return connect();
  }, [connect, socket.current]);

  useEffect(() => {
    if (socketError && autoReconnect) {
      reconnect();
    }
  }, [socketError, autoReconnect]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case ScenarioPathsMessageTypes.UPDATE:
          const updateMessage = data as ScenarioPathsUpdateMessage;
          setStatus(updateMessage.status);
          setMessage(updateMessage.message);
          // The consumer sends each ERROR on its own, so a later UPDATE replaces it.
          setServiceError(null);
          break;
        case ScenarioPathsMessageTypes.ERROR:
          const errorMessage = data as ScenarioPathsErrorMessage;
          setStatus(ScenarioPathsStatus.ERROR);
          setServiceError(errorMessage.message);
          analytics.logEvent(AnalyticsEvent.SCENARIO_PATHS_ERROR, {
            scenarioId,
            error: errorMessage,
          });
          break;
        case ScenarioPathsMessageTypes.PROGRESS:
          const progressMessage = data as ScenarioPathsProgressMessage;
          setProgress(progressMessage.progress);
          setStatus(progressMessage.status);
          break;
        case ScenarioPathsMessageTypes.PING:
          // Just a response to our ping, no action needed
          break;
        default:
          console.warn('Unknown message type:', data.type);
      }
      clearTimeout(keepAliveTimeout.current);
      if (socket.current && socket.current.readyState === WebSocket.OPEN) {
        pingServer();
      }
    },
    [socket.current, pingServer],
  );

  useEffect(() => {
    if (!socket.current) return;

    socket.current.onerror = (event: Event) => {
      console.error('WebSocket error:', event);
      setStatus(ScenarioPathsStatus.ERROR);
      setSocketError('WebSocket connection error');
    };

    socket.current.onmessage = handleMessage;
  }, [socket.current?.readyState, handleMessage]);

  const build = useCallback(() => {
    if (socket.current && socket.current.readyState === WebSocket.OPEN) {
      socket.current.send(
        JSON.stringify({ action: ScenarioPathsActions.BUILD }),
      );
      setProgress(IDLE_PROGRESS);
      setStatus(ScenarioPathsStatus.RUNNING);
      analytics.logEvent(AnalyticsEvent.BUILT_PATH_SERVICE, {
        scenarioId,
      });
    }
  }, [socket.current, analytics]);

  useEffect(() => {
    if (status === ScenarioPathsStatus.SUCCESS) {
      setProgress(READY_PROGRESS);
    }
  }, [status]);

  const result = useMemo<UseScenarioPathsResult>(() => {
    return {
      build,
      ready:
        socket.current?.readyState === WebSocket.OPEN && isLoading === false,
      progress,
      status,
      message,
      serviceError,
      socketError,
    };
  }, [
    build,
    socket.current?.readyState,
    progress,
    status,
    message,
    serviceError,
    socketError,
    isLoading,
  ]);

  return result;
};
